<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import Badge from '../lib/ui/Badge.svelte';
  import Button from '../lib/ui/Button.svelte';
  import Card from '../lib/ui/Card.svelte';
  import Notice from '../lib/ui/Notice.svelte';
  import Pager from '../lib/ui/Pager.svelte';
  import ReportSendDeliveriesTable from './ReportSendDeliveriesTable.svelte';
  import { ReportDefinitionSends } from '../lib/report-definition-sends.svelte';
  import { ReportLineTenant } from '../lib/report-line-tenant';
  import { ReportSchedule } from '../lib/report-schedule';
  import { ReportSendOverview } from '../lib/report-send-overview';
  import { ReportSendsEmptyNote } from '../lib/report-sends-empty-note';
  import type { ReportDefinitionGroup, ReportDefinitionSummary } from '../lib/api';
  import type { ReportDefinitionsBoard } from '../lib/report-definitions-board.svelte';

  interface Props {
    board: ReportDefinitionsBoard;
    group: ReportDefinitionGroup;
    definition: ReportDefinitionSummary;
  }

  let { board, group, definition }: Props = $props();

  const loader = untrack(() => new ReportDefinitionSends(board));
  const page = $derived(loader.page);
  const tenant = $derived(ReportLineTenant.of(definition));
  const schedule = $derived(new ReportSchedule(definition.schedule));

  onMount(() => void loader.load(definition, 1));
</script>

<nav class="crumbs">
  <Button size="sm" onclick={() => board.openLine(definition)}>← {tenant.name}</Button>
  <span class="sub">{group.name} · {tenant.name} · {schedule.describe()}</span>
</nav>

<Card title="Envios" count={page?.total}>
  {#if loader.failedBeforeAnyPage}
    <div class="empty">
      <p>Não deu para carregar os envios.</p>
      <Button size="sm" onclick={() => void loader.load(definition, 1)}>Tentar de novo</Button>
    </div>
  {:else if page === null}
    <p class="empty">Carregando os envios…</p>
  {:else if page.total === 0}
    <p class="empty">{ReportSendsEmptyNote.of(definition, new Date())}</p>
  {:else}
    {#if loader.failedPage !== null}
      {@const failedPage = loader.failedPage}
      <div class="page-failure">
        <Notice tone="danger">
          Não deu para carregar a página {failedPage}.
          <Button size="sm" onclick={() => void loader.load(definition, failedPage)}>Tentar de novo</Button>
        </Notice>
      </div>
    {/if}
    <ul class="sends">
      {#each page.items as send (send.reportId)}
        {@const overview = new ReportSendOverview(send)}
        {@const open = loader.expanded === send.reportId}
        <li>
          <button class="row" aria-expanded={open} aria-controls="envio-{send.reportId}" onclick={() => loader.toggle(send.reportId)}>
            <span class="caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
            <span class="when">
              <span class="name">{overview.when}</span>
              {#if overview.period}<span class="sub">{overview.period}</span>{/if}
            </span>
            <Badge tone={overview.summary.tone} dot>{overview.summary.label}</Badge>
          </button>
          <div id="envio-{send.reportId}">
            {#if open}
              <ReportSendDeliveriesTable {board} {definition} {send} />
            {/if}
          </div>
        </li>
      {/each}
    </ul>
    {#if page.total > page.perPage}
      <Pager page={page.page} total={page.total} perPage={page.perPage} noun="envios" label="Páginas de envios" onGo={(next) => void loader.load(definition, next)} />
    {/if}
    <p class="sub note">E-mail vai até "Aberto", que conta também o leitor que baixa a imagem sozinho (Apple Mail, Gmail). WhatsApp vai até "Lido".</p>
  {/if}
</Card>

<style>
  .crumbs {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-4);
  }
  .sends {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li {
    border-top: 1px solid var(--border-subtle);
  }
  li:first-child {
    border-top: none;
  }
  .row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    padding: var(--space-3) var(--space-4);
    text-align: left;
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    transition: background var(--duration-fast) var(--easing-default);
  }
  .row:hover {
    background: var(--bg-hover);
  }
  .caret {
    color: var(--text-tertiary);
  }
  .when {
    display: flex;
    gap: var(--space-3);
    align-items: baseline;
  }
  .name {
    color: var(--text-primary);
    font-weight: 500;
  }
  .sub {
    color: var(--text-tertiary);
    font-size: 12px;
  }
  .page-failure {
    margin-bottom: var(--space-3);
  }
  .empty p {
    margin: 0;
  }
  .note {
    margin: var(--space-4) 0 0;
  }
  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-3);
    margin: 0;
    padding: var(--space-6) 0;
    text-align: center;
    color: var(--text-tertiary);
    font-size: 12px;
  }
</style>
